import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchWaTemplates } from "../../../Slices/whatsappSlice";
import { autoFillValue, renderTemplate, VARIABLE_SOURCE_LABELS } from "./waUtils";

const CATEGORY_CHIP = { MARKETING: "wa-chip--warn", UTILITY: "wa-chip--accent", AUTHENTICATION: "" };

/**
 * Template list + variable form + live preview. Controlled by the parent via
 * onChange({ template, values, valid }). Used by the template picker panel and
 * the "New message" panel.
 *
 * fillContext: { contactName, recordName, senderName }
 * compliance:  { optInStatus, hasPriorInbound, emailEngaged, emailEngagementRequired }
 */
const TemplateComposer = ({ fillContext, compliance, onChange }) => {
  const dispatch = useDispatch();
  const { items, loading, error, loaded } = useSelector((s) => s.whatsapp.templates);
  const canManage = useSelector((s) => s.whatsapp.status?.canManage);
  const [selectedKey, setSelectedKey] = useState(null);
  const [values, setValues] = useState([]);

  useEffect(() => {
    if (!loaded && !loading) dispatch(fetchWaTemplates());
  }, [dispatch, loaded, loading]);

  const sorted = useMemo(
    () => [...items].sort((a, b) => Number(b.sendable) - Number(a.sendable) || a.name.localeCompare(b.name)),
    [items]
  );
  const keyOf = (t) => `${t.name}|${t.language}`;
  const template = sorted.find((t) => keyOf(t) === selectedKey) || null;

  // Select the first sendable template once loaded.
  useEffect(() => {
    if (!selectedKey && sorted.length) {
      const first = sorted.find((t) => t.sendable) || sorted[0];
      setSelectedKey(keyOf(first));
    }
  }, [sorted, selectedKey]);

  // Pre-fill variables whenever the template or the context changes.
  useEffect(() => {
    if (!template) return;
    setValues(template.variables.map((v) => autoFillValue(v.source, fillContext || {})));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, fillContext?.contactName, fillContext?.recordName, fillContext?.senderName]);

  const isMarketing = template?.category === "MARKETING";
  const marketingNeedsOptIn = isMarketing && !!compliance && compliance.optInStatus !== "opted_in";
  const marketingNeedsEngagement =
    isMarketing && !marketingNeedsOptIn && compliance?.emailEngagementRequired !== false && compliance?.emailEngaged === false;
  const marketingBlocked = marketingNeedsOptIn || marketingNeedsEngagement;
  const optInBlocked = compliance && compliance.optInStatus !== "opted_in" && !compliance.hasPriorInbound;
  const optedOut = compliance?.optInStatus === "opted_out";
  const valid = !!template && template.sendable && values.length === template.variables.length && values.every((v) => String(v || "").trim());

  useEffect(() => {
    onChange?.({ template, values, valid, blocked: optedOut || optInBlocked || marketingBlocked });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, values, valid, optedOut, optInBlocked, marketingBlocked]);

  if (loading && !items.length) {
    return <div className="wa-empty"><i className="fa-solid fa-spinner wa-spin wa-empty__icon" /> Loading approved templates…</div>;
  }
  if (error && !items.length) {
    return (
      <div className="wa-empty">
        <i className="fa-solid fa-triangle-exclamation wa-empty__icon" />
        <div className="wa-empty__title">Couldn't load templates</div>
        <div>{error}</div>
        <button type="button" className="wa-btn wa-btn--sm" onClick={() => dispatch(fetchWaTemplates())}>Retry</button>
      </div>
    );
  }
  if (!items.length) {
    return (
      <div className="wa-empty">
        <i className="fa-regular fa-file-lines wa-empty__icon" />
        <div className="wa-empty__title">No approved templates</div>
        <div>Create and get templates approved in WhatsApp Manager — they appear here automatically.</div>
      </div>
    );
  }

  return (
    <div className="wa-tpl-grid">
      <div className="wa-tpl-list" role="listbox" aria-label="Approved templates">
        {sorted.map((t) => (
          <button
            type="button"
            key={keyOf(t)}
            role="option"
            aria-selected={keyOf(t) === selectedKey}
            className={`wa-tpl-item ${keyOf(t) === selectedKey ? "is-active" : ""} ${t.sendable ? "" : "is-disabled"}`}
            onClick={() => setSelectedKey(keyOf(t))}
          >
            <b>{t.name}</b>
            <span className={`wa-chip ${CATEGORY_CHIP[t.category] || ""}`}>{(t.category || "").toLowerCase()}</span>{" "}
            <span className="wa-chip">{t.language}</span>
          </button>
        ))}
        {canManage && (
          <button type="button" className="wa-btn wa-btn--sm wa-btn--ghost" onClick={() => dispatch(fetchWaTemplates({ refresh: true }))} disabled={loading}>
            <i className={`fa-solid fa-rotate ${loading ? "wa-spin" : ""}`} /> Refresh from Meta
          </button>
        )}
      </div>

      <div>
        {template && (
          <>
            {!template.sendable && (
              <div className="wa-alert wa-alert--warn" style={{ marginBottom: 12 }}><i className="fa-solid fa-triangle-exclamation" /><div>{template.unsupportedReason}</div></div>
            )}
            {optedOut && (
              <div className="wa-alert wa-alert--danger" style={{ marginBottom: 12 }}><i className="fa-solid fa-ban" /><div>This contact opted out (STOP). They must reply START before you can message them.</div></div>
            )}
            {!optedOut && optInBlocked && (
              <div className="wa-alert wa-alert--warn" style={{ marginBottom: 12 }}><i className="fa-solid fa-user-check" /><div>No WhatsApp consent on record and they have never messaged us. Record their opt-in first.</div></div>
            )}
            {!optedOut && marketingNeedsOptIn && (
              <div className="wa-alert wa-alert--warn" style={{ marginBottom: 12 }}>
                <i className="fa-solid fa-bullhorn" />
                <div>Marketing templates need a recorded WhatsApp opt-in (Meta policy). Record their opt-in in the details panel, or use a utility template.</div>
              </div>
            )}
            {!optedOut && marketingNeedsEngagement && (
              <div className="wa-alert wa-alert--warn" style={{ marginBottom: 12 }}>
                <i className="fa-solid fa-bullhorn" />
                <div>
                  Your team's rule: marketing templates only go to leads who opened, clicked or replied to our email. Link this chat to the lead, use a utility template,
                  or {canManage ? "turn the rule off in WhatsApp Admin → Templates" : "ask a WhatsApp admin to turn the rule off"}.
                </div>
              </div>
            )}
            {template.variables.map((v, i) => (
              <div className="wa-field" key={`${v.component}-${v.key}`}>
                <label className="wa-label" htmlFor={`wa-var-${i}`}>
                  {v.label} <span style={{ color: "var(--wa-faint)", fontWeight: 400 }}>{`{{${v.key}}}`}</span>
                </label>
                <input
                  id={`wa-var-${i}`}
                  className="wa-input"
                  value={values[i] || ""}
                  maxLength={300}
                  onChange={(e) => setValues((prev) => prev.map((p, j) => (j === i ? e.target.value : p)))}
                  placeholder={VARIABLE_SOURCE_LABELS[v.source] || "Value"}
                  disabled={!template.sendable}
                />
              </div>
            ))}
            <div className="wa-label" style={{ marginTop: 6 }}>Preview</div>
            <div className="wa-preview">
              <div className="wa-msg wa-msg--out">
                <div className="wa-bubble">
                  <div className="wa-bubble__tpl"><i className="fa-solid fa-file-lines" /> {template.name}</div>
                  <div className="wa-bubble__text">{renderTemplate(template, values)}</div>
                  {template.buttons?.length > 0 && (
                    <div style={{ borderTop: "1px solid var(--wa-line)", marginTop: 8, paddingTop: 6, display: "flex", flexDirection: "column", gap: 4 }}>
                      {template.buttons.map((b, i) => (
                        <span key={i} style={{ color: "var(--wa-accent)", fontWeight: 600, textAlign: "center", fontSize: 12.5 }}>{b.text}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TemplateComposer;
